import React from "react";
import {
  Scale,
  PlayCircle,
  FileText,
  ShieldCheck,
  Monitor,
  Clock,
  Landmark,
  Globe,
} from "lucide-react";

const PLAYFAIR = "'Playfair Display', Georgia, serif";
const INTER = "'Inter', system-ui, -apple-system, sans-serif";
const GOLD = "#B08A45";
const GOLD_DARK = "#8a6a2f";
const NAVY = "#0F172A";
const CANVAS = "#FAF8F4";

/* -------------------------------------------------------------------------- */
/*  Reusable pieces                                                           */
/* -------------------------------------------------------------------------- */

function FeatureCard({ icon: Icon, title, children, delay = 0 }) {
  return (
    <div
      className="group flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm transition-all duration-500 hover:-translate-y-0.5 hover:border-[#B08A45]/40 hover:bg-white/[0.07]"
      style={{
        animation: `fadeSlideUp 700ms ${delay}ms cubic-bezier(.2,.7,.2,1) both`,
      }}
      data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-105"
        style={{
          background:
            "linear-gradient(135deg, rgba(176,138,69,0.28), rgba(176,138,69,0.10))",
          boxShadow: "inset 0 0 0 1px rgba(176,138,69,0.35)",
        }}
      >
        <Icon className="h-5 w-5" style={{ color: GOLD }} />
      </div>
      <div className="min-w-0">
        <div
          className="text-[15px] font-semibold text-white"
          style={{ fontFamily: INTER, letterSpacing: "0.01em" }}
        >
          {title}
        </div>
        <p
          className="mt-1 text-[13px] leading-relaxed text-white/70"
          style={{ fontFamily: INTER }}
        >
          {children}
        </p>
      </div>
    </div>
  );
}

function BottomBadge({ icon: Icon, label }) {
  return (
    <div
      className="flex items-center gap-2 whitespace-nowrap"
      data-testid={`badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span
        className="flex h-6 w-6 items-center justify-center rounded-full"
        style={{
          background: "rgba(176,138,69,0.14)",
          boxShadow: "inset 0 0 0 1px rgba(176,138,69,0.45)",
        }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: GOLD_DARK }} />
      </span>
      <span
        className="text-[13px] font-medium text-stone-700"
        style={{ fontFamily: INTER }}
      >
        {label}
      </span>
    </div>
  );
}

function GoogleGlyph({ className = "h-5 w-5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c3 0 5.7 1.1 7.8 3l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.7 0 19.5-8.7 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.7 19 12.5 24 12.5c3 0 5.7 1.1 7.8 3l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 16.2 4.5 9.5 8.8 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.4 2.2-7.1 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.4 39.1 16.1 43.5 24 43.5z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.1 5c-.4.4 6.7-4.9 6.7-14.4 0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/*  Login Page                                                                */
/* -------------------------------------------------------------------------- */

export default function LoginPage() {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openDemo = () =>
    window.open("https://youtu.be/i33ldxXrxyg", "_blank", "noopener,noreferrer");

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: CANVAS, fontFamily: INTER }}
      data-testid="login-page"
    >
      {/* keyframes + fonts (page-scoped) */}
      <style>{`
        @keyframes fadeSlideUp {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatBlob {
          0%, 100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(24px,-18px) scale(1.06); }
        }
        @keyframes floatBlobAlt {
          0%, 100% { transform: translate(0,0) scale(1); }
          50%      { transform: translate(-20px,22px) scale(1.04); }
        }
        @keyframes shine {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
        .ads-shine::after {
          content: "";
          position: absolute; inset: 0;
          background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%);
          transform: translateX(-120%);
          transition: transform .8s ease;
        }
        .ads-shine:hover::after { animation: shine 900ms ease forwards; }
      `}</style>

      {/* ---------- Ambient background: gold blobs + radial + scales watermark */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div
          className="absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(176,138,69,0.35), transparent 70%)",
            animation: "floatBlob 12s ease-in-out infinite",
          }}
        />
        <div
          className="absolute -right-24 top-40 h-[420px] w-[420px] rounded-full opacity-60 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(176,138,69,0.22), transparent 70%)",
            animation: "floatBlobAlt 14s ease-in-out infinite",
          }}
        />
        <div
          className="absolute bottom-[-120px] left-1/3 h-[360px] w-[360px] rounded-full opacity-50 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(15,23,42,0.10), transparent 70%)",
            animation: "floatBlob 16s ease-in-out infinite",
          }}
        />
        {/* Subtle scales-of-justice watermark on right canvas */}
        <Scale
          className="absolute right-[6%] top-[38%] hidden lg:block"
          style={{
            width: 380,
            height: 380,
            color: GOLD,
            opacity: 0.05,
            transform: "rotate(-6deg)",
          }}
        />
      </div>

      {/* ---------- Split layout container */}
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1400px] flex-col lg:flex-row">
        {/* =============================== LEFT (45%) =============================== */}
        <aside
          className="relative flex w-full flex-col justify-between overflow-hidden px-8 py-10 sm:px-12 lg:w-[45%] lg:px-14 lg:py-14"
          style={{ background: NAVY }}
          data-testid="login-left-panel"
        >
          {/* background image + gradient overlay */}
          <div className="absolute inset-0" aria-hidden="true">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-[0.22]"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1600&q=80')",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(160deg, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.88) 45%, rgba(15,23,42,0.96) 100%)",
              }}
            />
            {/* faint gold accent line */}
            <div
              className="absolute right-0 top-0 h-full w-[2px]"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, rgba(176,138,69,0.45), transparent)",
              }}
            />
          </div>

          {/* Top: brand */}
          <div
            className="relative z-10 flex items-center gap-3"
            style={{ animation: "fadeSlideUp 700ms 0ms both" }}
          >
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl"
              style={{
                background:
                  "linear-gradient(135deg, #C89A4E 0%, #8a6a2f 100%)",
                boxShadow: "0 8px 24px rgba(176,138,69,0.35)",
              }}
            >
              <Scale className="h-5 w-5 text-white" />
            </div>
            <div className="text-white/90">
              <div
                className="text-[13px] font-semibold tracking-[0.22em] uppercase"
                style={{ color: GOLD, fontFamily: INTER }}
              >
                Advocate Studio
              </div>
            </div>
          </div>

          {/* Middle: heading + subtitle + feature cards */}
          <div className="relative z-10 mt-14 lg:mt-0">
            <h1
              className="text-white"
              style={{
                fontFamily: PLAYFAIR,
                fontSize: "clamp(2.25rem, 3.4vw, 3.4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.01em",
                animation: "fadeSlideUp 800ms 80ms both",
              }}
            >
              Welcome to
              <br />
              <span style={{ color: GOLD }}>Advocate</span>{" "}
              <span className="text-white">Document Studio</span>
            </h1>

            <p
              className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/70"
              style={{
                fontFamily: INTER,
                animation: "fadeSlideUp 800ms 180ms both",
              }}
            >
              Create Court-Ready Legal Documents in Minutes.
            </p>

            <div className="mt-8 grid gap-3 sm:mt-10">
              <FeatureCard icon={FileText} title="Create Documents" delay={280}>
                Vakalatnama, Address Memo, Adjournment Application &amp; More.
              </FeatureCard>
              <FeatureCard icon={ShieldCheck} title="Secure & Private" delay={360}>
                Your advocate profile and documents are encrypted end-to-end.
              </FeatureCard>
              <FeatureCard icon={Monitor} title="Access Anywhere" delay={440}>
                Works seamlessly on Desktop, Tablet and Mobile.
              </FeatureCard>
            </div>
          </div>

          {/* Bottom: footnote */}
          <div
            className="relative z-10 mt-10 text-[12px] tracking-wide text-white/40"
            style={{
              fontFamily: INTER,
              animation: "fadeSlideUp 900ms 560ms both",
            }}
          >
            © {new Date().getFullYear()} Advocate Document Studio · Crafted for legal professionals
          </div>
        </aside>

        {/* =============================== RIGHT (55%) =============================== */}
        <main
          className="relative flex w-full flex-1 items-center justify-center px-6 py-12 sm:px-10 lg:w-[55%] lg:px-14"
          data-testid="login-right-panel"
        >
          <div
            className="relative w-full max-w-[520px]"
            style={{ animation: "fadeSlideUp 800ms 120ms both" }}
          >
            {/* Glassmorphism card */}
            <div
              className="relative overflow-hidden rounded-[24px] border border-white/60 bg-white/80 p-8 sm:p-10 backdrop-blur-xl"
              style={{
                boxShadow:
                  "0 30px 60px -20px rgba(15,23,42,0.18), 0 8px 24px -12px rgba(176,138,69,0.18)",
              }}
              data-testid="login-card"
            >
              {/* Decorative gold ring in corner */}
              <div
                className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full"
                aria-hidden="true"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(176,138,69,0.22), transparent 70%)",
                }}
              />

              {/* Circular gold logo */}
              <div className="mb-6 flex justify-center">
                <div
                  className="relative flex h-16 w-16 items-center justify-center rounded-full"
                  style={{
                    background:
                      "linear-gradient(135deg, #E1B76A 0%, #B08A45 55%, #8a6a2f 100%)",
                    boxShadow:
                      "0 10px 24px -6px rgba(176,138,69,0.55), inset 0 -2px 6px rgba(0,0,0,0.15)",
                  }}
                >
                  <Scale className="h-7 w-7 text-white" />
                  <span
                    className="absolute -inset-1 -z-10 rounded-full opacity-70 blur-md"
                    style={{ background: "rgba(176,138,69,0.35)" }}
                  />
                </div>
              </div>

              {/* Heading */}
              <h2
                className="text-center text-stone-900"
                style={{
                  fontFamily: PLAYFAIR,
                  fontSize: "clamp(1.6rem, 2.2vw, 2rem)",
                  letterSpacing: "-0.01em",
                }}
              >
                Sign in to your account
              </h2>
              <p
                className="mt-2 text-center text-[14px] leading-relaxed text-stone-600"
                style={{ fontFamily: INTER }}
              >
                Access your saved advocate profile, case history and generated
                documents securely.
              </p>

              {/* Primary: Continue with Google */}
              <button
                type="button"
                onClick={handleLogin}
                data-testid="btn-google-signin"
                className="ads-shine group relative mt-8 flex h-14 w-full items-center justify-center gap-3 overflow-hidden rounded-2xl font-semibold text-white transition-all duration-300 hover:-translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  background:
                    "linear-gradient(135deg, #C89A4E 0%, #B08A45 55%, #8a6a2f 100%)",
                  boxShadow:
                    "0 14px 28px -12px rgba(176,138,69,0.65), inset 0 1px 0 rgba(255,255,255,0.35)",
                  fontFamily: INTER,
                }}
              >
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white"
                  style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.12)" }}
                >
                  <GoogleGlyph className="h-4 w-4" />
                </span>
                <span className="text-[15px] tracking-wide">
                  Continue with Google
                </span>
              </button>

              {/* Secondary: Watch App Demo */}
              <button
                type="button"
                onClick={openDemo}
                data-testid="btn-demo-video"
                className="group mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-stone-200 bg-white text-stone-700 transition-all duration-300 hover:-translate-y-[1px] hover:border-stone-300 hover:bg-stone-50"
                style={{ fontFamily: INTER, boxShadow: "0 4px 10px -6px rgba(15,23,42,0.10)" }}
              >
                <PlayCircle
                  className="h-5 w-5 transition-transform duration-300 group-hover:scale-110"
                  style={{ color: "#DC2626" }}
                />
                <span className="text-[14px] font-medium">Watch App Demo</span>
              </button>

              {/* Security info card */}
              <div
                className="mt-6 flex items-start gap-3 rounded-2xl p-4"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(176,138,69,0.06) 0%, rgba(176,138,69,0.02) 100%)",
                  boxShadow: "inset 0 0 0 1px rgba(176,138,69,0.18)",
                }}
                data-testid="security-card"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                  style={{
                    background: "rgba(176,138,69,0.14)",
                    boxShadow: "inset 0 0 0 1px rgba(176,138,69,0.35)",
                  }}
                >
                  <ShieldCheck className="h-4 w-4" style={{ color: GOLD_DARK }} />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-[13.5px] font-semibold text-stone-800"
                    style={{ fontFamily: INTER }}
                  >
                    Your data is secure
                  </div>
                  <p
                    className="mt-0.5 text-[12.5px] leading-relaxed text-stone-600"
                    style={{ fontFamily: INTER }}
                  >
                    We never share your information.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <p
                className="mt-6 text-center text-[11.5px] leading-relaxed text-stone-500"
                style={{ fontFamily: INTER }}
              >
                By continuing, you agree to securely store your advocate profile
                and case history in your Google account.
              </p>
            </div>

            {/* Floating bottom bar */}
            <div
              className="relative mx-auto mt-6 w-full max-w-[520px]"
              style={{ animation: "fadeSlideUp 900ms 320ms both" }}
            >
              <div
                className="flex flex-wrap items-center justify-around gap-x-5 gap-y-3 rounded-2xl bg-white/85 px-4 py-3 backdrop-blur-md"
                style={{
                  boxShadow:
                    "0 20px 40px -20px rgba(15,23,42,0.15), inset 0 0 0 1px rgba(255,255,255,0.7)",
                }}
                data-testid="features-bar"
              >
                <BottomBadge icon={ShieldCheck} label="100% Secure" />
                <BottomBadge icon={Clock} label="Save Time" />
                <BottomBadge icon={Landmark} label="Court Ready" />
                <BottomBadge icon={Globe} label="Works Everywhere" />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
